// docs/116: the inn's panels over the village view (VillageView mounts this inside its overlays while
// my band is in the inn or upstairs). The action bar, the bard's music, the toast line, and whichever
// place's panel is open (`useInn.panel`): the dice table close up, the noticeboard, the guestbook, the
// window, the bar, the bard, the room. Esc steps back from a panel unless a modal is up over it.
import { useEffect } from 'react';
import type { InnData } from './types';
import type { VillageLook } from '../types';
import { useInn } from './store';
import { BardDirector } from './BardDirector';
import { DiceTable } from './DiceTable';
import { InnBar, UpstairsBar } from './panels/InnBar';
import { ToastLine } from './panels/ToastLine';
import { BarPanel } from './panels/BarPanel';
import { BardPanel } from './panels/BardPanel';
import { RoomPanel } from './panels/RoomPanel';
import { NoticeBoard } from './panels/NoticeBoard';
import { Guestbook } from './panels/Guestbook';
import { WindowView } from './panels/WindowView';
import { closePanel } from './panels/common';
import './panels.css';

export interface InnOverlaysProps { data: InnData; look: VillageLook; upstairs: boolean }

export function InnOverlays({ data, look, upstairs }: InnOverlaysProps): React.JSX.Element | null {
  const panel = useInn((s) => s.panel);
  const diceMode = useInn((s) => s.diceMode);
  useEffect(() => {
    if (!panel) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || document.querySelector('[aria-modal="true"]')) return;
      e.preventDefault();
      closePanel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [panel]);
  // going up or down the stairs puts down whatever was open
  useEffect(() => { useInn.getState().close(); }, [upstairs]);

  const shown = upstairs ? undefined : panel;
  return (
    <>
      <BardDirector data={data} look={look} upstairs={upstairs} />
      {upstairs ? <UpstairsBar data={data} /> : <InnBar data={data} />}
      {!upstairs && <ToastLine cheers={data.cheers} />}
      {shown === 'dice' && data.dice && data.diceActions && (
        <DiceTable view={data.dice} actions={data.diceActions} payers={data.payers} me="" mode={diceMode} spectators={[]} onClose={closePanel} />
      )}
      {shown === 'notice' && <NoticeBoard data={data} look={look} />}
      {shown === 'guestbook' && <Guestbook data={data} look={look} />}
      {shown === 'window' && <WindowView look={look} />}
      {shown === 'bar' && <BarPanel data={data} seed={look.seed + data.week} />}
      {shown === 'bard' && <BardPanel data={data} look={look} />}
      {shown === 'room' && <RoomPanel data={data} />}
    </>
  );
}
